import { useMemo, useRef, useState, type CSSProperties } from 'react';

import { usePageTitle } from '../hooks/usePageTitle';
import './MojitoMixer.css';

type IngredientId = 'mint' | 'lime' | 'syrup' | 'rum' | 'soda' | 'ice';

interface Ingredient {
  id: IngredientId;
  name: string;
  /** Noun for the running count, e.g. "8 leaves". */
  unit: string;
  /** Same noun for a count of one, e.g. "1 leaf". */
  unitOne: string;
  emoji: string;
  action: string;
  /** Volume added to the glass per unit, in ml. */
  ml: number;
  /** Colour this ingredient pushes the drink toward. */
  rgb: [number, number, number];
  /** Chuck's spec for The O.G. Mojito. */
  target: number;
  /** Share of the 100-point score this ingredient is worth. */
  weight: number;
}

const INGREDIENTS: Ingredient[] = [
  {
    id: 'mint',
    name: 'Fresh Mint',
    unit: 'leaves',
    unitOne: 'leaf',
    emoji: '🌿',
    action: 'Drop in a leaf',
    ml: 2,
    rgb: [126, 196, 122],
    target: 10,
    weight: 20,
  },
  {
    id: 'lime',
    name: 'Half Limes',
    unit: 'halves',
    unitOne: 'half',
    emoji: '🟢',
    action: 'Squeezed with Lime Squeezer',
    ml: 15,
    rgb: [186, 222, 106],
    target: 2,
    weight: 15,
  },
  {
    id: 'syrup',
    name: 'Mint Infused Simple Syrup',
    unit: 'pumps',
    unitOne: 'pump',
    emoji: '🍯',
    action: 'Pump in syrup',
    ml: 10,
    rgb: [199, 226, 156],
    target: 2,
    weight: 15,
  },
  {
    id: 'rum',
    name: 'White Rum',
    unit: 'oz',
    unitOne: 'oz',
    emoji: '🥃',
    action: 'Free pour an oz',
    ml: 30,
    rgb: [235, 226, 196],
    target: 2,
    weight: 25,
  },
  {
    id: 'soda',
    name: 'Lime Flavored Soda Water',
    unit: 'splashes',
    unitOne: 'splash',
    emoji: '🫧',
    action: 'Top with soda',
    ml: 25,
    rgb: [214, 240, 248],
    target: 3,
    weight: 15,
  },
  {
    id: 'ice',
    name: 'Clear Ice Cubes',
    unit: 'cubes',
    unitOne: 'cube',
    emoji: '🧊',
    action: 'Drop in a cube',
    ml: 12,
    rgb: [232, 248, 255],
    target: 2,
    weight: 10,
  },
];

/** Glass capacity in ml. Past this, Chuck is mopping. */
const CAPACITY = 340;
const MAX_PER_INGREDIENT = 20;

const EMPTY_COUNTS: Record<IngredientId, number> = {
  mint: 0,
  lime: 0,
  syrup: 0,
  rum: 0,
  soda: 0,
  ice: 0,
};

type NoteStatus = 'good' | 'over' | 'under' | 'missing';

interface Note {
  id: string;
  status: NoteStatus;
  text: string;
}

interface Result {
  score: number;
  stars: number;
  headline: string;
  verdict: string;
  notes: Note[];
}

/** "1 leaf" / "10 leaves" — nobody serves "1 leaves". */
function unitFor(ingredient: Ingredient, count: number): string {
  return count === 1 ? ingredient.unitOne : ingredient.unit;
}

/** Deterministic 0–1 "randomness" so solids don't jump around on every render. */
function jitter(index: number, seed: number): number {
  return ((index * 9301 + seed * 49297 + 233) % 233280) / 233280;
}

/* ── SVG glass geometry ─────────────────────────────────────────────────────
   The glass is drawn in a 240×410 viewBox: a tapered highball whose inner
   cavity runs from the rim (y=30) down to the floor of the drink (y=340).
   Everything liquid-related is positioned in these coordinates. */
const GLASS_CX = 120;
const RIM_Y = 30;
const LIQUID_TOP = 46;
const INNER_BOTTOM = 340;
const OUTER_PATH = 'M 56 30 L 65 336 Q 66 354 84 354 L 156 354 Q 174 354 175 336 L 184 30 Z';
const INNER_PATH = 'M 64 30 L 72 330 Q 73 342 87 342 L 153 342 Q 167 342 168 330 L 176 30 Z';

/** Inner half-width of the tapered cavity at a given y. */
function halfWidthAt(y: number): number {
  return 55 - ((y - RIM_Y) / (INNER_BOTTOM - RIM_Y)) * 8;
}

/** X coordinate for a solid at fraction `frac` (0–1) across the cavity at height y. */
function solidX(frac: number, y: number): number {
  return GLASS_CX + (frac * 2 - 1) * (halfWidthAt(y) - 14);
}

/** Condensation droplet positions on the outside of the glass — static, jittered. */
const DROPLETS = Array.from({ length: 16 }, (_, i) => {
  const y = 168 + jitter(i, 43) * 158;
  return {
    x: GLASS_CX + (jitter(i, 41) * 2 - 1) * (halfWidthAt(y) + 1),
    y,
    r: 1.3 + jitter(i, 47) * 2.1,
  };
});

function buildResult(
  counts: Record<IngredientId, number>,
  muddled: boolean,
  rumBeforeMuddle: boolean,
  overflowed: boolean,
): Result {
  const notes: Note[] = [];
  let score = 0;

  for (const ingredient of INGREDIENTS) {
    const count = counts[ingredient.id];
    const { target, weight, name } = ingredient;
    const deviation = Math.min(Math.abs(count - target) / target, 1);
    const unit = unitFor(ingredient, count);
    score += (1 - deviation) * weight;

    if (count === 0) {
      notes.push({
        id: ingredient.id,
        status: 'missing',
        text: `No ${name.toLowerCase()} at all. A mojito without it is just a sad glass.`,
      });
    } else if (deviation <= 0.15) {
      notes.push({
        id: ingredient.id,
        status: 'good',
        text: `${name}: ${count} ${unit} — dead on spec.`,
      });
    } else if (count > target) {
      notes.push({
        id: ingredient.id,
        status: 'over',
        text: `${name}: ${count} ${unit}. Spec is ${target}. Heavy hand.`,
      });
    } else {
      notes.push({
        id: ingredient.id,
        status: 'under',
        text: `${name}: ${count} ${unit}. Spec is ${target}. Don't be shy.`,
      });
    }
  }

  if (!muddled) {
    score -= 25;
    notes.push({
      id: 'muddle',
      status: 'missing',
      text: 'You never muddled. The mint is intact and deeply offended.',
    });
  }

  if (rumBeforeMuddle) {
    score -= 15;
    notes.push({
      id: 'order',
      status: 'over',
      text: 'Rum went in before the muddle. Chuck saw that. Chuck says nothing.',
    });
  }

  if (overflowed) {
    score -= 10;
    notes.push({
      id: 'overflow',
      status: 'over',
      text: 'The glass overflowed. There is a puddle. It is your puddle.',
    });
  }

  score = Math.max(0, Math.round(score));
  const stars = Math.max(1, Math.min(5, Math.ceil(score / 20)));

  let headline: string;
  let verdict: string;

  if (score >= 90) {
    headline = 'Chuck is nodding. Slowly.';
    verdict =
      "That is a Chuck's Mojito. The ratio holds, the mint is bruised not shredded, and nothing here needs explaining. Highest praise currently available.";
  } else if (score >= 75) {
    headline = 'Solid pour.';
    verdict =
      "You'd get a shift on a Tuesday. Not a Friday. But a Tuesday, and that's a real job with real mojitos.";
  } else if (score >= 55) {
    headline = 'Drinkable. Barely.';
    verdict =
      'Chuck has notes. Chuck always has notes, but this time Chuck wrote them down, which is worse.';
  } else if (score >= 30) {
    headline = 'This is a mint-flavored incident.';
    verdict =
      'Independent lab analysis returns only the classification: "inconclusive — requires further sampling." Nobody is volunteering to sample.';
  } else {
    headline = 'Chuck has quietly taken the glass away from you.';
    verdict =
      'No lecture. No eye contact. Just a fresh glass sliding across the bar and a new chance at dignity.';
  }

  return { score, stars, headline, verdict, notes };
}

export default function MojitoMixer() {
  usePageTitle("Mix Your Own — Chuck's Mojito Shack");

  const [counts, setCounts] = useState<Record<IngredientId, number>>({ ...EMPTY_COUNTS });
  const [muddled, setMuddled] = useState(false);
  const [muddling, setMuddling] = useState(false);
  const [rumBeforeMuddle, setRumBeforeMuddle] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const muddleTimer = useRef<number | null>(null);

  const totalMl = useMemo(
    () => INGREDIENTS.reduce((sum, item) => sum + counts[item.id] * item.ml, 0),
    [counts],
  );
  const overflowed = totalMl > CAPACITY;
  const fillPercent = Math.min(100, (totalMl / CAPACITY) * 100);
  const isEmpty = totalMl === 0;

  /** Weighted blend of every ingredient's colour, by volume in the glass. */
  const liquidColor = useMemo(() => {
    let r = 236;
    let g = 244;
    let b = 236;
    let weight = 0.001;

    for (const ingredient of INGREDIENTS) {
      const volume = counts[ingredient.id] * ingredient.ml;
      if (volume === 0) continue;
      r += ingredient.rgb[0] * volume;
      g += ingredient.rgb[1] * volume;
      b += ingredient.rgb[2] * volume;
      weight += volume;
    }

    return [r / weight, g / weight, b / weight].map(Math.round) as [number, number, number];
  }, [counts]);

  const [lr, lg, lb] = liquidColor;
  /** The liquid colour at a brightness multiple, as an rgba() string. */
  const tint = (m: number, a: number) =>
    `rgba(${Math.round(Math.min(255, lr * m))}, ${Math.round(Math.min(255, lg * m))}, ${Math.round(
      Math.min(255, lb * m),
    )}, ${a})`;

  /** Where the liquid surface sits in SVG coordinates, and how deep the pour is. */
  const surfaceY = INNER_BOTTOM - (fillPercent / 100) * (INNER_BOTTOM - LIQUID_TOP);
  const depth = INNER_BOTTOM - surfaceY;

  const canMuddle = !muddled && counts.mint > 0 && counts.lime > 0 && counts.syrup > 0;
  const locked = result !== null;

  const addIngredient = (ingredient: Ingredient) => {
    if (locked) return;
    if (counts[ingredient.id] >= MAX_PER_INGREDIENT) return;

    if (ingredient.id === 'rum' && !muddled) setRumBeforeMuddle(true);
    setCounts((current) => ({ ...current, [ingredient.id]: current[ingredient.id] + 1 }));
  };

  const muddle = () => {
    if (!canMuddle || muddling || locked) return;
    setMuddling(true);
    muddleTimer.current = window.setTimeout(() => {
      setMuddled(true);
      setMuddling(false);
    }, 1100);
  };

  const serve = () => {
    if (isEmpty || locked) return;
    setResult(buildResult(counts, muddled, rumBeforeMuddle, overflowed));
  };

  const reset = () => {
    if (muddleTimer.current !== null) window.clearTimeout(muddleTimer.current);
    setCounts({ ...EMPTY_COUNTS });
    setMuddled(false);
    setMuddling(false);
    setRumBeforeMuddle(false);
    setResult(null);
  };

  const stage = muddled ? 'Muddled' : canMuddle ? 'Ready to muddle' : 'Building';

  return (
    <div className="mixer-page page-content">
      <div className="mixer-header">
        <span className="section-tag">The Simulator</span>
        <h1 className="mixer-title">Mix Your Own</h1>
        <p className="mixer-sub">
          Chuck won't write the recipe down, but he'll let you guess. Build the glass, muddle it,
          serve it, and find out exactly how wrong you were.
        </p>
      </div>

      <div className="mixer-lab">
        {/* ===== THE GLASS ===== */}
        <div className="mixer-stage">
          <div
            className={[
              'glass',
              muddling ? 'is-muddling' : '',
              muddled ? 'is-muddled' : '',
              overflowed ? 'is-overflowing' : '',
              result ? 'is-served' : '',
            ]
              .join(' ')
              .trim()}
          >
            <svg
              className="glass-svg"
              viewBox="0 0 240 410"
              role="img"
              aria-label={`Highball glass containing ${Math.round(totalMl)} ml`}
            >
              <defs>
                <linearGradient id="liquidGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={tint(1.08, 0.62)} />
                  <stop offset="45%" stopColor={tint(1, 0.84)} />
                  <stop offset="100%" stopColor={tint(0.76, 0.95)} />
                </linearGradient>
                <linearGradient id="glassWall" x1="0" y1="0" x2="1" y2="0.12">
                  <stop offset="0%" stopColor="rgba(255, 255, 255, 0.35)" />
                  <stop offset="45%" stopColor="rgba(255, 255, 255, 0.07)" />
                  <stop offset="100%" stopColor="rgba(255, 255, 255, 0.25)" />
                </linearGradient>
                <linearGradient id="shineGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(255, 255, 255, 0.9)" />
                  <stop offset="80%" stopColor="rgba(255, 255, 255, 0)" />
                </linearGradient>
                <linearGradient id="iceGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="rgba(255, 255, 255, 0.92)" />
                  <stop offset="55%" stopColor="rgba(203, 236, 250, 0.48)" />
                  <stop offset="100%" stopColor="rgba(255, 255, 255, 0.78)" />
                </linearGradient>
                <linearGradient id="mintGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8fd487" />
                  <stop offset="100%" stopColor="#4c9a51" />
                </linearGradient>
                <linearGradient id="woodGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#b07a41" />
                  <stop offset="100%" stopColor="#7c4e24" />
                </linearGradient>
                <radialGradient id="dropGrad" cx="0.35" cy="0.3" r="0.8">
                  <stop offset="0%" stopColor="rgba(255, 255, 255, 0.95)" />
                  <stop offset="100%" stopColor="rgba(255, 255, 255, 0.15)" />
                </radialGradient>
                <radialGradient id="shadowGrad">
                  <stop offset="0%" stopColor="rgba(62, 39, 35, 0.3)" />
                  <stop offset="100%" stopColor="rgba(62, 39, 35, 0)" />
                </radialGradient>
                <pattern
                  id="strawStripes"
                  width="12"
                  height="12"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(38)"
                >
                  <rect width="12" height="12" fill="#fff" />
                  <rect width="6" height="12" fill="var(--coral, #ff6f61)" />
                </pattern>
                <clipPath id="glassCavity">
                  <path d={INNER_PATH} />
                </clipPath>
                <path id="leafPath" d="M 0 -10 C 6.5 -7 7.5 1.5 0 10 C -7.5 1.5 -6.5 -7 0 -10 Z" />
              </defs>

              {/* Ground shadow */}
              <ellipse cx="120" cy="382" rx="64" ry="10" fill="url(#shadowGrad)" />

              {/* Glass body */}
              <path
                d={OUTER_PATH}
                fill="url(#glassWall)"
                stroke="rgba(255, 255, 255, 0.75)"
                strokeWidth="2.5"
                strokeLinejoin="round"
              />
              <path d={INNER_PATH} fill="rgba(255, 255, 255, 0.1)" />

              {/* The drink, clipped to the inner cavity */}
              {fillPercent > 0 && (
                <g clipPath="url(#glassCavity)">
                  <rect
                    className="liquid-rect"
                    x="58"
                    y={surfaceY}
                    width="124"
                    height={INNER_BOTTOM + 12 - surfaceY}
                    fill="url(#liquidGrad)"
                  />

                  {counts.soda > 0 &&
                    depth > 24 &&
                    Array.from({ length: Math.min(counts.soda * 4, 24) }, (_, i) => (
                      <circle
                        key={`bubble-${i}`}
                        className="bubble"
                        cx={solidX(jitter(i, 3), INNER_BOTTOM - 10)}
                        cy={INNER_BOTTOM - 8}
                        r={1.6 + jitter(i, 5) * 2.2}
                        style={
                          {
                            '--rise': `${-(depth - 18)}px`,
                            animationDelay: `${jitter(i, 7) * 2.4}s`,
                            animationDuration: `${1.8 + jitter(i, 11) * 1.6}s`,
                          } as CSSProperties
                        }
                      />
                    ))}

                  <ellipse
                    className="liquid-surface"
                    cx="120"
                    cy={surfaceY}
                    rx={Math.max(halfWidthAt(surfaceY) - 2, 0)}
                    ry="6.5"
                    fill={tint(1.18, 0.85)}
                  />

                  {Array.from({ length: Math.min(counts.mint, 14) }, (_, i) => {
                    const spread = muddled
                      ? Math.max(depth - 22, 0)
                      : Math.max(depth - 24, 0) * 0.5;
                    const y = Math.min(surfaceY + 8 + jitter(i, 6) * spread, INNER_BOTTOM - 9);
                    const x = solidX(jitter(i, 2), y);
                    const deg = Math.round(jitter(i, 9) * 360);
                    return (
                      <g
                        key={`mint-${i}`}
                        className="solid-g"
                        style={{ transform: `translate(${x}px, ${y}px) rotate(${deg}deg)` }}
                      >
                        <g className="mint-inner">
                          <use href="#leafPath" className="mint-body" />
                          <path className="mint-vein" d="M 0 -7 L 0 7" />
                        </g>
                      </g>
                    );
                  })}

                  {Array.from({ length: Math.min(counts.lime, 10) }, (_, i) => {
                    const y = Math.min(
                      surfaceY + depth * 0.3 + jitter(i, 8) * depth * 0.55,
                      INNER_BOTTOM - 10,
                    );
                    const x = solidX(jitter(i, 4), y);
                    const deg = Math.round(jitter(i, 13) * 180 - 90);
                    return (
                      <g
                        key={`lime-${i}`}
                        className="solid-g"
                        style={{ transform: `translate(${x}px, ${y}px) rotate(${deg}deg)` }}
                      >
                        <g className="lime-inner">
                          <path
                            d="M -12 3 A 12 12 0 0 1 12 3 Z"
                            fill="#cfe97c"
                            stroke="#679a2e"
                            strokeWidth="2.5"
                            strokeLinejoin="round"
                          />
                          <path d="M -9.5 2.5 A 9.5 9.5 0 0 1 9.5 2.5 Z" fill="rgba(255, 255, 255, 0.55)" />
                          <path d="M -8 2 A 8 8 0 0 1 8 2 Z" fill="#d8ef8f" />
                          <path
                            d="M 0 2 L 0 -6 M 0 2 L -5.5 -4 M 0 2 L 5.5 -4"
                            stroke="rgba(255, 255, 255, 0.8)"
                            strokeWidth="1.2"
                            fill="none"
                          />
                        </g>
                      </g>
                    );
                  })}

                  {counts.syrup > 0 && (
                    <ellipse
                      className="syrup-pool"
                      cx="120"
                      cy={INNER_BOTTOM - 5}
                      rx={halfWidthAt(INNER_BOTTOM - 5) - 4}
                      ry={4 + Math.min(counts.syrup, 6) * 1.5}
                      fill="rgba(207, 232, 164, 0.8)"
                    />
                  )}

                  {Array.from({ length: Math.min(counts.ice, 12) }, (_, i) => {
                    const y = Math.min(
                      surfaceY + 12 + jitter(i, 29) * Math.min(depth * 0.4, 52),
                      INNER_BOTTOM - 12,
                    );
                    const x = solidX(jitter(i, 23), y);
                    const deg = Math.round(jitter(i, 31) * 60 - 30);
                    return (
                      <g
                        key={`ice-${i}`}
                        className="solid-g"
                        style={{ transform: `translate(${x}px, ${y}px) rotate(${deg}deg)` }}
                      >
                        <g
                          className="ice-inner"
                          style={{ animationDelay: `${jitter(i, 37) * 2.8}s` }}
                        >
                          <path className="ice-face" d="M -11 -8 L 8 -12 L 13 7 L -7 12 Z" />
                          <path className="ice-crack" d="M -4 -8 L 2 6" />
                        </g>
                      </g>
                    );
                  })}
                </g>
              )}

              {/* Condensation on the outside once ice is in */}
              <g className={`condensation${counts.ice > 0 ? ' is-visible' : ''}`} aria-hidden="true">
                {DROPLETS.map((d, i) => (
                  <ellipse key={i} cx={d.x} cy={d.y} rx={d.r} ry={d.r * 1.35} fill="url(#dropGrad)" />
                ))}
              </g>

              {/* Reflections, rim and base */}
              <path
                d="M 73 46 L 79 316"
                stroke="url(#shineGrad)"
                strokeWidth="10"
                strokeLinecap="round"
                fill="none"
                opacity="0.65"
              />
              <path
                d="M 165 46 L 161 316"
                stroke="url(#shineGrad)"
                strokeWidth="5"
                strokeLinecap="round"
                fill="none"
                opacity="0.4"
              />
              <ellipse
                cx="120"
                cy="30"
                rx="64"
                ry="7"
                fill="rgba(255, 255, 255, 0.14)"
                stroke="rgba(255, 255, 255, 0.85)"
                strokeWidth="2"
              />
              <ellipse cx="120" cy="348" rx="53" ry="8" fill="rgba(216, 240, 246, 0.5)" />
              <path
                d="M 65 336 Q 66 354 84 354 L 156 354 Q 174 354 175 336"
                fill="none"
                stroke="rgba(255, 255, 255, 0.65)"
                strokeWidth="2.5"
              />

              {/* Muddler, mid-muddle */}
              {muddling && (
                <g className="muddler" transform="rotate(7 120 60)" aria-hidden="true">
                  <g className="muddler-plunge">
                    <rect x="113" y="-30" width="14" height="185" rx="7" fill="url(#woodGrad)" />
                    <rect
                      x="107"
                      y="150"
                      width="26"
                      height="28"
                      rx="9"
                      fill="url(#woodGrad)"
                      stroke="rgba(93, 64, 28, 0.4)"
                    />
                  </g>
                </g>
              )}

              {/* Overflow drips and puddle */}
              {overflowed && (
                <g aria-hidden="true">
                  <path
                    className="drip"
                    d="M 60 34 q -3 24 -1 48"
                    stroke={tint(1, 0.75)}
                    strokeWidth="5"
                    strokeLinecap="round"
                    fill="none"
                  />
                  <path
                    className="drip drip-2"
                    d="M 180 36 q 3 28 0 56"
                    stroke={tint(1, 0.7)}
                    strokeWidth="4"
                    strokeLinecap="round"
                    fill="none"
                  />
                  <ellipse className="puddle" cx="120" cy="376" rx="70" ry="9" fill={tint(0.95, 0.45)} />
                </g>
              )}

              {/* Garnish, added on serve */}
              {result && (
                <g className="garnish-g" aria-hidden="true">
                  <g transform="rotate(14 152 40)">
                    <rect
                      x="147"
                      y="-52"
                      width="11"
                      height="200"
                      rx="5.5"
                      fill="url(#strawStripes)"
                      stroke="rgba(0, 0, 0, 0.08)"
                    />
                    <ellipse
                      cx="152.5"
                      cy="-51"
                      rx="5.5"
                      ry="2.4"
                      fill="#ffe9e5"
                      stroke="rgba(0, 0, 0, 0.1)"
                    />
                  </g>
                  <g transform="translate(181 30)">
                    <circle r="16" fill="#d9ec8b" stroke="#679a2e" strokeWidth="3.5" />
                    <circle r="11.5" fill="none" stroke="rgba(255, 255, 255, 0.85)" strokeWidth="1.6" />
                    {Array.from({ length: 6 }, (_, i) => (
                      <line
                        key={i}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="-10.5"
                        transform={`rotate(${i * 60})`}
                        stroke="rgba(255, 255, 255, 0.8)"
                        strokeWidth="1.4"
                      />
                    ))}
                  </g>
                  <g className="sprig-g" transform="translate(76 24)">
                    <path d="M 0 12 L 0 0" stroke="#3f7a3d" strokeWidth="2" />
                    <use href="#leafPath" transform="translate(0 -8) scale(1.05)" fill="url(#mintGrad)" />
                    <use href="#leafPath" transform="translate(-9 0) rotate(-42) scale(0.9)" fill="url(#mintGrad)" />
                    <use href="#leafPath" transform="translate(9 0) rotate(42) scale(0.9)" fill="url(#mintGrad)" />
                    <use href="#leafPath" transform="translate(-7 8) rotate(-70) scale(0.75)" fill="#5d9b57" />
                    <use href="#leafPath" transform="translate(7 8) rotate(70) scale(0.75)" fill="#5d9b57" />
                  </g>
                </g>
              )}
            </svg>
          </div>

          <div className="mixer-readout" aria-live="polite">
            <div className="readout-row">
              <span className="readout-label">In the glass</span>
              <span className="readout-value">
                {Math.round(totalMl)} ml{overflowed && <em> — overflowing!</em>}
              </span>
            </div>
            <div className="readout-row">
              <span className="readout-label">Stage</span>
              <span className="readout-value">{muddling ? 'Muddling…' : stage}</span>
            </div>
          </div>
        </div>

        {/* ===== THE BAR RAIL ===== */}
        <div className="mixer-controls">
          <h2 className="controls-heading">The Rail</h2>
          <div className="ingredient-grid">
            {INGREDIENTS.map((ingredient) => {
              const count = counts[ingredient.id];
              return (
                <button
                  key={ingredient.id}
                  type="button"
                  className={`ingredient-btn${count > 0 ? ' has-some' : ''}`}
                  onClick={() => addIngredient(ingredient)}
                  disabled={locked || count >= MAX_PER_INGREDIENT}
                  aria-label={`${ingredient.action}. Currently ${count} ${unitFor(ingredient, count)}.`}
                >
                  <span className="ingredient-emoji" aria-hidden="true">
                    {ingredient.emoji}
                  </span>
                  <span className="ingredient-text">
                    <strong>{ingredient.name}</strong>
                    <small>{ingredient.action}</small>
                  </span>
                  <span className="ingredient-count">
                    {count}
                    <small>{unitFor(ingredient, count)}</small>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mixer-actions">
            <button
              type="button"
              className="action-btn muddle-btn"
              onClick={muddle}
              disabled={!canMuddle || muddling || locked}
            >
              {muddled ? '✔ Muddled' : muddling ? 'Muddling…' : '🪵 Muddle It'}
            </button>
            <button
              type="button"
              className="action-btn serve-btn"
              onClick={serve}
              disabled={isEmpty || muddling || locked}
            >
              🍹 Serve to Chuck
            </button>
            <button type="button" className="action-btn reset-btn" onClick={reset}>
              ♻️ Fresh Glass
            </button>
          </div>

          {!muddled && !canMuddle && !locked && (
            <p className="mixer-hint">
              Mint, lime and syrup go in first — then you can muddle. Everything after that is
              between you and your conscience.
            </p>
          )}
        </div>
      </div>

      {/* ===== THE VERDICT ===== */}
      {result && (
        <div className="verdict" role="status">
          <div className="verdict-score">
            <span className="verdict-number">{result.score}</span>
            <span className="verdict-outof">/ 100</span>
            <span className="verdict-stars" aria-label={`${result.stars} out of 5 limes`}>
              {'🟢'.repeat(result.stars)}
              <span className="stars-empty">{'⚪'.repeat(5 - result.stars)}</span>
            </span>
          </div>

          <h2 className="verdict-headline">{result.headline}</h2>
          <p className="verdict-text">{result.verdict}</p>

          <ul className="verdict-notes">
            {result.notes.map((note) => (
              <li key={note.id} className={`note note-${note.status}`}>
                {note.text}
              </li>
            ))}
          </ul>

          <div className="verdict-spec">
            <h3>Chuck's Actual Spec — The O.G. Mojito</h3>
            <p className="spec-warning">
              This is the part Chuck doesn't write down. It is written down now. Do not tell him.
            </p>
            <div className="spec-grid">
              {INGREDIENTS.map((ingredient) => (
                <div key={ingredient.id} className="spec-item">
                  <span className="spec-emoji" aria-hidden="true">
                    {ingredient.emoji}
                  </span>
                  <span className="spec-amount">
                    {ingredient.target} {unitFor(ingredient, ingredient.target)}
                  </span>
                  <span className="spec-name">{ingredient.name}</span>
                </div>
              ))}
            </div>
            <p className="spec-order">
              Order of operations: mint, lime and syrup into the glass → muddle → rum → ice → soda.
            </p>
          </div>

          <button type="button" className="action-btn serve-btn verdict-again" onClick={reset}>
            Pour Another
          </button>
        </div>
      )}
    </div>
  );
}
